import { FC, FocusEvent, ReactNode } from 'react';
import { Controller, Control } from 'react-hook-form';
import { Input, theme } from 'antd';
import { ErrorText } from '../Elements';
import FormLabel from './FormLabel';

const { useToken } = theme;

interface InputFieldProps {
  label?: string;
  type?: 'text' | 'number' | 'password';
  placeholder?: string;
  control: Control<any>;
  name: string;
  error?: string;
  readonly?: boolean;
  disabled?: boolean;
  variant?: 'outlined' | 'borderless' | 'filled';
  prefix?: string | ReactNode;
  suffix?: string | ReactNode;
  size?: 'large' | 'middle' | 'small';
  formDirection?: 'row' | 'column';
  required?: boolean;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  visibilityToggle?: {
    visible: boolean;
    onVisibleChange: (visible: boolean) => void;
  };
  style?: React.CSSProperties;
  subLabel?: string;
  allowDecimal?: boolean;
  maxLength?: number;
}

const InputField: FC<InputFieldProps> = ({
  name,
  control,
  label,
  type = 'text',
  placeholder,
  variant = 'outlined',
  readonly = false,
  disabled = false,
  error,
  prefix,
  suffix,
  size = 'middle',
  formDirection = 'column',
  required = false,
  onBlur,
  visibilityToggle,
  style,
  subLabel,
  allowDecimal = false,
  maxLength,
}) => {
  const errorStatus = error ? 'error' : undefined;
  // const { token } = useToken();
  return (
    <div className={`form-control ${formDirection}`} style={style}>
      {label && (
        <FormLabel required={required} label={label} subLabel={subLabel} />
      )}
      <div className="input-containter">
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            if (type === 'password') {
              return (
                <Input.Password
                  {...field}
                  prefix={prefix}
                  suffix={suffix}
                  maxLength={maxLength}
                  type={type}
                  readOnly={readonly}
                  variant={variant}
                  placeholder={placeholder}
                  status={errorStatus}
                  size={size}
                  disabled={disabled}
                  onBlur={onBlur}
                  visibilityToggle={visibilityToggle}
                />
              );
            } else if (type === 'number' && !allowDecimal) {
              const formatNumber = (num: string | number) => {
                if (num === '' || num === undefined || num === null) return '';
                const number =
                  typeof num === 'string'
                    ? parseFloat(num.replace(/,/g, ''))
                    : num;
                if (isNaN(number)) return '';
                return number.toLocaleString();
              };

              const parseNumber = (str: string) => {
                const parsed = str.replace(/,/g, '').replace(/[a-zA-Z]/g, '');
                return isNaN(Number(parsed)) ? '' : parsed;
              };

              return (
                <Input
                  {...field}
                  value={formatNumber(field.value)}
                  maxLength={maxLength}
                  onChange={(e) => field.onChange(parseNumber(e.target.value))}
                  prefix={prefix}
                  suffix={suffix}
                  readOnly={readonly}
                  variant={variant}
                  placeholder={placeholder}
                  status={errorStatus}
                  size={size}
                  disabled={disabled}
                  onBlur={onBlur}
                  inputMode="decimal"
                />
              );
            } else if (type === 'number' && allowDecimal) {
              return (
                <Input
                  {...field}
                  value={field.value}
                  maxLength={maxLength}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Accept only numbers and a single optional decimal point
                    if (/^-?\d*(\.\d{0,10})?$/.test(val)) {
                      field.onChange(val);
                    }
                  }}
                  prefix={prefix}
                  suffix={suffix}
                  readOnly={readonly}
                  variant={variant}
                  placeholder={placeholder}
                  status={errorStatus}
                  size={size}
                  disabled={disabled}
                  onBlur={onBlur}
                  inputMode="decimal"
                />
              );
            } else {
              return (
                <Input
                  {...field}
                  prefix={prefix}
                  suffix={suffix}
                  maxLength={maxLength}
                  type={type}
                  readOnly={readonly}
                  variant={variant}
                  placeholder={placeholder}
                  status={errorStatus}
                  size={size}
                  disabled={disabled}
                  onBlur={onBlur}
                />
              );
            }
          }}
        />
        {error && <ErrorText error={error} />}
      </div>
    </div>
  );
};

export default InputField;
